// Admin → user detail. Lives at /users/:id.
//
// Five tabs (router-driven, bookmarkable URLs) over a single API document
// returned by GET /api/admin/users/:id. Mutations call the dedicated
// state-change endpoints and patch local state; the audit-trail tab
// re-fetches to surface the new entry without a full reload.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  toast,
} from "@bloom/ui";
import {
  ArrowLeft,
  Ban,
  CheckCircle2,
  LogOut,
  ShieldAlert,
  ShieldCheck,
  UserCheck,
} from "lucide-react";
import { api, ApiError } from "../api/client.ts";
import { useAuth } from "../auth/auth-context.tsx";

interface UserDetail {
  user: {
    _id: string;
    email: string;
    name: string | null;
    locale: "th" | "en" | null;
    role: string;
    emailVerified: boolean;
    banned: boolean;
    banReason: string | null;
    banExpires: string | null;
    twoFactorEnabled: boolean;
    totalSpendThb: number;
    purchaseCount: number;
    lastSeenAt: string | null;
    createdAt: string;
  };
  stats: { episodesWatched: number; completedEpisodes: number; distinctSeries: number };
  recentPurchases: Array<{
    id: string;
    kind: "pack" | "bundle";
    seriesId: string;
    seriesTitleEn: string | null;
    seriesTitleTh: string | null;
    amountThb: number;
    paidAt: string;
  }>;
  recentWatch: Array<{
    episodeId: string;
    seriesId: string;
    seriesTitleEn: string | null;
    seriesTitleTh: string | null;
    seasonNumber: number;
    episodeNumber: number;
    episodeTitleEn: string;
    positionSec: number;
    durationSec: number;
    progressPct: number;
    completed: boolean;
    updatedAt: string;
  }>;
  activeSessions: Array<{
    sessionId: string;
    token: string | null;
    ipAddress: string | null;
    userAgent: string | null;
    expiresAt: string | null;
    createdAt: string | null;
    lastSeenAt: string | null;
    impersonatedBy: string | null;
  }>;
  auditTrail: Array<{
    id: string;
    at: string;
    actorId: string;
    actorEmail: string | null;
    actorRole: string;
    action: string;
    before: unknown;
    after: unknown;
  }>;
}

const TABS = [
  { value: "profile", label: "Profile" },
  { value: "purchases", label: "Purchases" },
  { value: "watch", label: "Watch history" },
  { value: "sessions", label: "Sessions" },
  { value: "audit", label: "Audit log" },
] as const;

type TabValue = (typeof TABS)[number]["value"];

const ROLES = ["user", "support", "content_manager", "finance", "admin", "superadmin"] as const;

export function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const isSuperadmin = me?.role === "superadmin";
  const { hash } = useLocation();
  const activeTab: TabValue = useMemo(() => {
    const t = hash.replace("#", "") as TabValue;
    return TABS.some((x) => x.value === t) ? t : "profile";
  }, [hash]);

  const [data, setData] = useState<UserDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const fresh = await api.get<UserDetail>(`/api/admin/users/${id}`);
      setData(fresh);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
      if (err instanceof ApiError && err.status === 404) {
        toast.error("User not found");
      }
    }
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (error && !data) {
    return (
      <div className="space-y-3">
        <Link
          to="/users"
          className="inline-flex items-center gap-1 text-sm text-[var(--brand-rose)] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Back to users
        </Link>
        <p className="text-sm text-rose-700">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-32 animate-pulse rounded bg-[var(--brand-pink-soft)]/40" />
        <div className="h-32 animate-pulse rounded-2xl bg-[var(--brand-pink-soft)]/40" />
        <div className="h-64 animate-pulse rounded-2xl bg-[var(--brand-pink-soft)]/40" />
      </div>
    );
  }

  const u = data.user;
  const initials = (u.name ?? u.email).slice(0, 2).toUpperCase();
  const goTab = (t: TabValue) => navigate(`/users/${u._id}#${t}`);

  return (
    <div className="space-y-6">
      <Link
        to="/users"
        className="inline-flex items-center gap-1 text-sm text-[var(--brand-rose)] hover:underline"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Back to users
      </Link>

      {/* Header card */}
      <header className="flex flex-wrap items-start gap-4 rounded-2xl border border-[var(--brand-border)] bg-white p-5">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[var(--brand-pink-soft)] font-mono text-lg font-bold text-[var(--brand-ink)]">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-2xl font-bold text-[var(--brand-ink)]">{u.email}</h1>
          <p className="text-sm text-[var(--brand-muted)]">
            {u.name ?? "—"} · joined{" "}
            {new Date(u.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
            {u.lastSeenAt
              ? ` · last seen ${new Date(u.lastSeenAt).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}`
              : ""}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <RolePill role={u.role} />
            {u.emailVerified ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">
                <ShieldCheck className="h-3 w-3" aria-hidden /> verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">
                unverified
              </span>
            )}
            {u.banned ? (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-rose-700"
                title={u.banReason ?? ""}
              >
                <ShieldAlert className="h-3 w-3" aria-hidden /> banned
              </span>
            ) : null}
            {u.twoFactorEnabled ? (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">2FA on</span>
            ) : null}
          </div>
        </div>
        <div className="grid gap-1 text-right">
          <p className="font-mono text-2xl font-bold text-[var(--brand-ink)]">
            ฿{u.totalSpendThb.toLocaleString()}
          </p>
          <p className="text-xs text-[var(--brand-muted)]">
            {u.purchaseCount} purchase{u.purchaseCount === 1 ? "" : "s"} ·{" "}
            {data.stats.episodesWatched} eps watched
          </p>
        </div>
      </header>

      {/* Tab strip */}
      <nav className="inline-flex items-center gap-1 rounded-full bg-[var(--brand-pink-soft)] p-1 text-sm">
        {TABS.map((tab) => {
          const active = activeTab === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => goTab(tab.value)}
              className={cn(
                "rounded-full px-4 py-1.5 transition-colors",
                active
                  ? "bg-[var(--brand-ink)] text-white"
                  : "text-[var(--brand-ink)]/70 hover:text-[var(--brand-ink)]",
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </nav>

      {/* Panels */}
      <div>
        {activeTab === "profile" ? (
          <ProfileTab data={data} isSuperadmin={isSuperadmin} onReload={reload} />
        ) : null}
        {activeTab === "purchases" ? <PurchasesTab data={data} /> : null}
        {activeTab === "watch" ? <WatchTab data={data} /> : null}
        {activeTab === "sessions" ? <SessionsTab data={data} onReload={reload} /> : null}
        {activeTab === "audit" ? <AuditTab data={data} /> : null}
      </div>
    </div>
  );
}

function ProfileTab({
  data,
  isSuperadmin,
  onReload,
}: {
  data: UserDetail;
  isSuperadmin: boolean;
  onReload: () => Promise<void>;
}) {
  const u = data.user;
  const [savingRole, setSavingRole] = useState(false);
  const [pendingRole, setPendingRole] = useState(u.role);

  const handleBan = async () => {
    const reason = window.prompt(`Ban ${u.email}? Enter a reason:`);
    if (!reason) return;
    try {
      await api.post(`/api/admin/users/${u._id}/ban`, { reason });
      toast.success(`${u.email} is banned`);
      await onReload();
    } catch (err) {
      toast.error(`Ban failed: ${(err as Error).message}`);
    }
  };

  const handleUnban = async () => {
    try {
      await api.post(`/api/admin/users/${u._id}/unban`);
      toast.success(`${u.email} is unbanned`);
      await onReload();
    } catch (err) {
      toast.error(`Unban failed: ${(err as Error).message}`);
    }
  };

  const handleRevokeAll = async () => {
    if (!window.confirm(`Force log-out ${u.email} on every device?`)) return;
    try {
      await api.post(`/api/admin/users/${u._id}/sessions/revoke-all`);
      toast.success("All sessions revoked");
      await onReload();
    } catch (err) {
      toast.error(`Revoke failed: ${(err as Error).message}`);
    }
  };

  const handleSetRole = async () => {
    if (pendingRole === u.role) return;
    setSavingRole(true);
    try {
      await api.post(`/api/admin/users/${u._id}/role`, { role: pendingRole });
      toast.success(`Role updated to ${pendingRole}`);
      await onReload();
    } catch (err) {
      toast.error(`Role update failed: ${(err as Error).message}`);
      setPendingRole(u.role);
    } finally {
      setSavingRole(false);
    }
  };

  const handleImpersonate = async () => {
    if (
      !window.confirm(
        `Take over ${u.email}'s session? Your admin session is suspended until you stop impersonating.`,
      )
    )
      return;
    try {
      const res = await api.post<{ redirectUrl: string; note: string }>(
        `/api/admin/users/${u._id}/impersonate`,
      );
      window.open(res.redirectUrl, "_blank", "noopener");
      toast.success(`Impersonating ${u.email} in a new tab`);
    } catch (err) {
      toast.error(`Impersonate failed: ${(err as Error).message}`);
    }
  };

  return (
    <section className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-2xl border border-[var(--brand-border)] bg-white p-5">
        <h2 className="font-heading text-lg font-bold">Identity</h2>
        <dl className="mt-3 grid grid-cols-[max-content,1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-[var(--brand-muted)]">User ID</dt>
          <dd className="font-mono text-xs">{u._id}</dd>
          <dt className="text-[var(--brand-muted)]">Email</dt>
          <dd>{u.email}</dd>
          <dt className="text-[var(--brand-muted)]">Name</dt>
          <dd>{u.name ?? "—"}</dd>
          <dt className="text-[var(--brand-muted)]">UI locale</dt>
          <dd>{u.locale ?? "—"}</dd>
          <dt className="text-[var(--brand-muted)]">2FA</dt>
          <dd>{u.twoFactorEnabled ? "Enabled" : "—"}</dd>
          {u.banned ? (
            <>
              <dt className="text-rose-700">Ban reason</dt>
              <dd className="text-rose-700">{u.banReason ?? "—"}</dd>
            </>
          ) : null}
        </dl>
      </div>

      <div className="rounded-2xl border border-[var(--brand-border)] bg-white p-5">
        <h2 className="font-heading text-lg font-bold">Actions</h2>
        <p className="mt-1 text-xs text-[var(--brand-muted)]">
          Every action below is audit-logged.
        </p>
        <div className="mt-4 space-y-4">
          {isSuperadmin ? (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-muted)]">
                Role
              </label>
              <div className="mt-1 flex items-center gap-2">
                <Select value={pendingRole} onValueChange={setPendingRole}>
                  <SelectTrigger className="max-w-[14rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={() => void handleSetRole()}
                  disabled={savingRole || pendingRole === u.role}
                  size="sm"
                >
                  {savingRole ? "Saving…" : "Save role"}
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-xs uppercase tracking-wide text-[var(--brand-muted)]">Role</p>
              <RolePill role={u.role} />
              <p className="mt-1 text-xs text-[var(--brand-muted)]">
                Only superadmins can change roles.
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {u.banned ? (
              <Button variant="ghost" onClick={() => void handleUnban()}>
                <CheckCircle2 className="h-4 w-4 text-emerald-700" aria-hidden /> Unban
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => void handleBan()}>
                <Ban className="h-4 w-4 text-rose-700" aria-hidden /> Ban…
              </Button>
            )}
            <Button variant="ghost" onClick={() => void handleRevokeAll()}>
              <LogOut className="h-4 w-4" aria-hidden /> Force log-out
            </Button>
            {isSuperadmin ? (
              <Button variant="primary" onClick={() => void handleImpersonate()}>
                <UserCheck className="h-4 w-4" aria-hidden /> Impersonate
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function PurchasesTab({ data }: { data: UserDetail }) {
  if (data.recentPurchases.length === 0) {
    return (
      <p className="rounded-2xl border border-[var(--brand-border)] bg-white p-8 text-center text-sm text-[var(--brand-muted)]">
        No paid purchases yet.
      </p>
    );
  }
  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--brand-border)] bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--brand-border)] text-left text-xs font-semibold uppercase tracking-wide text-[var(--brand-muted)]">
            <th className="px-4 py-3">Series</th>
            <th className="px-4 py-3">Kind</th>
            <th className="px-4 py-3 text-right">Amount</th>
            <th className="px-4 py-3">Paid at</th>
            <th className="px-4 py-3 font-mono">Ref</th>
          </tr>
        </thead>
        <tbody>
          {data.recentPurchases.map((p) => (
            <tr key={p.id} className="border-b border-[var(--brand-border)] last:border-b-0">
              <td className="px-4 py-3">
                <span className="font-medium">{p.seriesTitleEn ?? "(unknown)"}</span>
                {p.seriesTitleTh ? (
                  <span className="ml-2 text-xs text-[var(--brand-muted)]">{p.seriesTitleTh}</span>
                ) : null}
              </td>
              <td className="px-4 py-3 text-xs uppercase tracking-wide text-[var(--brand-muted)]">
                {p.kind}
              </td>
              <td className="px-4 py-3 text-right font-mono">฿{p.amountThb.toLocaleString()}</td>
              <td className="px-4 py-3 text-xs text-[var(--brand-muted)]">
                {new Date(p.paidAt).toLocaleString()}
              </td>
              <td className="px-4 py-3 font-mono text-xs text-[var(--brand-muted)]">
                {p.id.slice(0, 10)}…
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WatchTab({ data }: { data: UserDetail }) {
  if (data.recentWatch.length === 0) {
    return (
      <p className="rounded-2xl border border-[var(--brand-border)] bg-white p-8 text-center text-sm text-[var(--brand-muted)]">
        No watch history yet.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-3 text-sm">
        <Stat label="Episodes watched" value={data.stats.episodesWatched} />
        <Stat label="Completed" value={data.stats.completedEpisodes} />
        <Stat label="Distinct series" value={data.stats.distinctSeries} />
      </div>
      <div className="overflow-hidden rounded-2xl border border-[var(--brand-border)] bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--brand-border)] text-left text-xs font-semibold uppercase tracking-wide text-[var(--brand-muted)]">
              <th className="px-4 py-3">Series · Episode</th>
              <th className="px-4 py-3 w-1/3">Progress</th>
              <th className="px-4 py-3">Updated</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.recentWatch.map((w) => (
              <tr
                key={w.episodeId}
                className="border-b border-[var(--brand-border)] last:border-b-0"
              >
                <td className="px-4 py-3">
                  <p className="font-medium">{w.seriesTitleEn ?? "(unknown)"}</p>
                  <p className="text-xs text-[var(--brand-muted)]">
                    S{w.seasonNumber}E{w.episodeNumber} · {w.episodeTitleEn}
                  </p>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--brand-pink-soft)]">
                      <div
                        className="h-full bg-[var(--brand-rose)]"
                        style={{ width: `${w.progressPct}%` }}
                      />
                    </div>
                    <span className="font-mono text-xs text-[var(--brand-muted)]">
                      {w.progressPct}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-[var(--brand-muted)]">
                  {new Date(w.updatedAt).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-xs">
                  {w.completed ? (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">
                      completed
                    </span>
                  ) : (
                    <span className="text-[var(--brand-muted)]">in progress</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SessionsTab({ data, onReload }: { data: UserDetail; onReload: () => Promise<void> }) {
  const u = data.user;
  const handleRevoke = async (token: string | null, sessionId: string) => {
    if (!window.confirm("Revoke this session?")) return;
    try {
      await api.post(`/api/admin/users/${u._id}/sessions/${sessionId}/revoke`, {
        token: token ?? sessionId,
      });
      toast.success("Session revoked");
      await onReload();
    } catch (err) {
      toast.error(`Revoke failed: ${(err as Error).message}`);
    }
  };
  if (data.activeSessions.length === 0) {
    return (
      <p className="rounded-2xl border border-[var(--brand-border)] bg-white p-8 text-center text-sm text-[var(--brand-muted)]">
        No active sessions.
      </p>
    );
  }
  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--brand-border)] bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--brand-border)] text-left text-xs font-semibold uppercase tracking-wide text-[var(--brand-muted)]">
            <th className="px-4 py-3">Session</th>
            <th className="px-4 py-3">IP</th>
            <th className="px-4 py-3">User agent</th>
            <th className="px-4 py-3">Expires</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {data.activeSessions.map((s) => (
            <tr key={s.sessionId} className="border-b border-[var(--brand-border)] last:border-b-0">
              <td className="px-4 py-3 font-mono text-xs">
                {s.sessionId.slice(0, 8)}…
                {s.impersonatedBy ? (
                  <span className="ml-2 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">
                    impersonated
                  </span>
                ) : null}
              </td>
              <td className="px-4 py-3 font-mono text-xs">{s.ipAddress ?? "—"}</td>
              <td className="px-4 py-3 text-xs text-[var(--brand-muted)]" title={s.userAgent ?? ""}>
                {s.userAgent ? s.userAgent.slice(0, 60) : "—"}
              </td>
              <td className="px-4 py-3 text-xs text-[var(--brand-muted)]">
                {s.expiresAt ? new Date(s.expiresAt).toLocaleString() : "—"}
              </td>
              <td className="px-4 py-3 text-right">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void handleRevoke(s.token, s.sessionId)}
                >
                  Revoke
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditTab({ data }: { data: UserDetail }) {
  if (data.auditTrail.length === 0) {
    return (
      <p className="rounded-2xl border border-[var(--brand-border)] bg-white p-8 text-center text-sm text-[var(--brand-muted)]">
        No admin actions on this user yet.
      </p>
    );
  }
  return (
    <ol className="space-y-2">
      {data.auditTrail.map((entry) => (
        <li
          key={entry.id || `${entry.at}-${entry.action}`}
          className="rounded-2xl border border-[var(--brand-border)] bg-white p-4 text-sm"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p>
              <span className="font-mono text-xs font-semibold text-[var(--brand-rose)]">
                {entry.action}
              </span>{" "}
              <span className="text-[var(--brand-muted)]">
                by {entry.actorEmail ?? entry.actorId.slice(0, 8)} ({entry.actorRole})
              </span>
            </p>
            <span className="text-xs text-[var(--brand-muted)]">
              {new Date(entry.at).toLocaleString()}
            </span>
          </div>
          {entry.before || entry.after ? (
            <pre className="mt-2 overflow-x-auto rounded-lg bg-[var(--brand-pink-soft)]/30 p-2 text-[11px] leading-snug text-[var(--brand-ink)]">
              {JSON.stringify({ before: entry.before, after: entry.after }, null, 2)}
            </pre>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-[var(--brand-border)] bg-white p-4 text-center">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-muted)]">
        {label}
      </p>
      <p className="mt-1 font-mono text-2xl font-bold text-[var(--brand-ink)]">{value}</p>
    </div>
  );
}

function RolePill({ role }: { role: string }) {
  const palette: Record<string, string> = {
    user: "bg-slate-100 text-slate-700",
    support: "bg-sky-100 text-sky-800",
    content_manager: "bg-violet-100 text-violet-800",
    finance: "bg-amber-100 text-amber-800",
    admin: "bg-rose-100 text-rose-800",
    superadmin: "bg-[var(--brand-ink)] text-white",
  };
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2 py-0.5 text-xs font-semibold",
        palette[role] ?? palette.user,
      )}
    >
      {role}
    </span>
  );
}
